import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/lib/auth';
import { RequireTeacher, RequireAdmin, RequireLearner, RequireStudent } from '@/components/Guards';
import ErrorBoundary from '@/components/ErrorBoundary';
import '@/App.css';

const Home = lazy(() => import('./features/public/pages/Home'));
const BecomeTutor = lazy(() => import('./features/public/pages/BecomeTutor'));
const Pricing = lazy(() => import('./features/public/pages/Pricing'));
const TutorsList = lazy(() => import('./features/directory/pages/TutorsList'));
const Landing = lazy(() => import('./features/directory/pages/Landing'));
const TutorProfilePage = lazy(() => import('./features/directory/pages/TutorProfilePage'));
const BookingFlow = lazy(() => import('./features/booking/pages/BookingFlow'));
const RequestTutor = lazy(() => import('./features/booking/pages/RequestTutor'));
const BookingConfirmed = lazy(() => import('./features/booking/pages/BookingConfirmed'));
const Signup = lazy(() => import('./features/auth/pages/Signup'));
const AccountHome = lazy(() => import('./features/account/pages/AccountHome'));
const AccountBooking = lazy(() => import('./features/account/pages/AccountBooking'));
const StudentHome = lazy(() => import('./features/account/pages/StudentHome'));
const TeacherAvailability = lazy(() => import('./features/teacher/pages/TeacherAvailability'));
const TeacherBookings = lazy(() => import('./features/teacher/pages/TeacherBookings'));
const AdminBookings = lazy(() => import('./features/admin/pages/AdminBookings'));
const AdminBookingDetail = lazy(() => import('./features/admin/pages/AdminBookingDetail'));
const About = lazy(() => import('./features/public/pages/About'));
const HowItWorks = lazy(() => import('./features/public/pages/HowItWorks'));
const Contact = lazy(() => import('./features/public/pages/Contact'));
const Legal = lazy(() => import('./features/public/pages/Legal'));
const Register = lazy(() => import('./features/auth/pages/Register'));
const Verify = lazy(() => import('./features/auth/pages/Verify'));
const Login = lazy(() => import('./features/auth/pages/Login'));
const Forgot = lazy(() => import('./features/auth/pages/Forgot'));
const Reset = lazy(() => import('./features/auth/pages/Reset'));
const TeacherDashboard = lazy(() => import('./features/teacher/pages/Dashboard'));
const TeacherProfile = lazy(() => import('./features/teacher/pages/Profile'));
const TeacherSettings = lazy(() => import('./features/teacher/pages/Settings'));
const AdminLogin = lazy(() => import('./features/admin/pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./features/admin/pages/AdminDashboard'));
const AdminTeachers = lazy(() => import('./features/admin/pages/AdminTeachers'));
const AdminTeacherDetail = lazy(() => import('./features/admin/pages/AdminTeacherDetail'));
const AdminCategories = lazy(() => import('./features/admin/pages/AdminCategories'));
const AdminEnquiries = lazy(() => import('./features/admin/pages/AdminEnquiries'));
const AdminAuditLog = lazy(() => import('./features/admin/pages/AdminAuditLog'));
const AdminUsers = lazy(() => import('./features/admin/pages/AdminUsers'));
const AdminRevisions = lazy(() => import('./features/admin/pages/AdminRevisions'));

function LoadingScreen() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="animate-fade-in text-muted-foreground text-sm">Loading…</div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" theme="light" richColors closeButton />
        <ErrorBoundary>
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/become-a-tutor" element={<BecomeTutor />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/tutors" element={<TutorsList />} />
              <Route path="/tutors/online" element={<Landing online />} />
              <Route path="/tutors/online/:subject" element={<Landing online />} />
              <Route path="/tutors/:city" element={<Landing />} />
              <Route path="/tutors/:city/:subject" element={<Landing />} />
              <Route path="/tutor/:slug" element={<TutorProfilePage />} />
              <Route path="/book/:slug" element={<BookingFlow />} />
              <Route path="/request-tutor" element={<RequestTutor />} />
              <Route path="/booking/:id/confirmed" element={<RequireLearner><BookingConfirmed /></RequireLearner>} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/account" element={<RequireLearner><AccountHome /></RequireLearner>} />
              <Route path="/account/bookings/:id" element={<RequireLearner><AccountBooking /></RequireLearner>} />
              <Route path="/student" element={<RequireStudent><StudentHome /></RequireStudent>} />
              <Route path="/about" element={<About />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/legal/:kind" element={<Legal />} />

              <Route path="/register" element={<Register />} />
              <Route path="/verify" element={<Verify />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<Forgot />} />
              <Route path="/reset-password" element={<Reset />} />

              <Route path="/teacher" element={<RequireTeacher><TeacherDashboard /></RequireTeacher>} />
              <Route path="/teacher/profile" element={<RequireTeacher><TeacherProfile /></RequireTeacher>} />
              <Route path="/teacher/availability" element={<RequireTeacher><TeacherAvailability /></RequireTeacher>} />
              <Route path="/teacher/bookings" element={<RequireTeacher><TeacherBookings /></RequireTeacher>} />
              <Route path="/teacher/settings" element={<RequireTeacher><TeacherSettings /></RequireTeacher>} />

              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
              <Route path="/admin/teachers" element={<RequireAdmin><AdminTeachers /></RequireAdmin>} />
              <Route path="/admin/teachers/:userId" element={<RequireAdmin><AdminTeacherDetail /></RequireAdmin>} />
              <Route path="/admin/categories" element={<RequireAdmin><AdminCategories /></RequireAdmin>} />
              <Route path="/admin/bookings" element={<RequireAdmin><AdminBookings /></RequireAdmin>} />
              <Route path="/admin/bookings/:id" element={<RequireAdmin><AdminBookingDetail /></RequireAdmin>} />
              <Route path="/admin/enquiries" element={<RequireAdmin><AdminEnquiries /></RequireAdmin>} />
              <Route path="/admin/audit-log" element={<RequireAdmin><AdminAuditLog /></RequireAdmin>} />
              <Route path="/admin/admins" element={<RequireAdmin><AdminUsers /></RequireAdmin>} />
              <Route path="/admin/revisions" element={<RequireAdmin><AdminRevisions /></RequireAdmin>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
